import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Upload } from "lucide-react";
import { mensagemDeErro } from "../../lib/erroApi";
import { copiaDoFicheiro } from "../../lib/copiaDoFicheiro";

// O piloto envia a licença e o certificado médico com a validade; o aluno envia o Cartão de Cidadão, o cartão de
// aluno e o certificado; o instrutor envia o Cartão de Cidadão, o certificado e a licença PU.
// Fica pendente até o DI aprovar. Um documento recusado é apagado, por isso não aparece em lado nenhum.
const erro = (e) => mensagemDeErro(e, "Não foi possível enviar.");

// Os documentos de cada tipo de ficha (o aluno não tem licença)
const CATEGORIAS = {
  aluno: [["cartao_cidadao", "Cartão de Cidadão (frente e verso no mesmo ficheiro)"], ["cartao_aluno", "Cartão de aluno"], ["certificado_medico", "Certificado médico"]],
  piloto: [["licenca", "Licença (PU ou PPL)"], ["certificado_medico", "Certificado médico"]],
  instrutor: [["cartao_cidadao", "Cartão de Cidadão (frente e verso no mesmo ficheiro)"], ["certificado_medico", "Certificado médico"], ["licenca_pu", "Licença PU (com a licença de instrutor no mesmo documento)"]],
};

export default function MeusDocumentosPiloto({ aluno = false, instrutor = false, onEnviado }) {
  const categorias = instrutor ? CATEGORIAS.instrutor : aluno ? CATEGORIAS.aluno : CATEGORIAS.piloto;
  const [categoria, setCategoria] = useState(categorias[0][0]);
  const [validade, setValidade] = useState("");
  const [validadeInstrutor, setValidadeInstrutor] = useState("");   // só a licença PU do instrutor tem esta segunda data
  const [ficheiro, setFicheiro] = useState(null);
  const [aGravar, setAGravar] = useState(false);
  const campoFicheiro = useRef(null);

  // Guarda uma cópia do ficheiro escolhido, lida logo para a memória (no iPhone o original pode chegar vazio ao servidor).
  // Não lê a data nem converte a foto: a validade preenche-se à mão.
  const escolherFicheiro = async (novo) => {
    if (!novo) { setFicheiro(null); return; }
    try {
      setFicheiro(await copiaDoFicheiro(novo));
    } catch (err) {
      setFicheiro(null);
      toast.error(err.message);
    }
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (!ficheiro) { toast.error("Escolhe o ficheiro (foto ou PDF)."); return; }
    if (!validade) { toast.error("Indica a validade que consta no documento."); return; }
    if (categoria === "licenca_pu" && !validadeInstrutor) { toast.error("Indica também a validade da licença de instrutor."); return; }
    const corpo = new FormData();
    corpo.append("file", ficheiro);
    corpo.append("categoria", categoria);
    corpo.append("valid_until", validade);
    if (categoria === "licenca_pu") corpo.append("valid_until_instrutor", validadeInstrutor);
    setAGravar(true);
    try {
      const destino = instrutor ? "/di/documents/instrutores/me/pedido" : "/di/documents/pilotos/me/pedido";
      await axios.post(`${API}${destino}`, corpo, { withCredentials: true });
      toast.success("Enviado. Fica a aguardar a aprovação do DI.");
      setFicheiro(null);
      setValidade("");
      setValidadeInstrutor("");
      e.target.reset();
      onEnviado?.();
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setAGravar(false);
    }
  };

  return (
    <div className="mt-3 border-t border-slate-800 pt-3 space-y-3 text-xs" data-testid="meus-documentos">
      <form onSubmit={enviar} className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="enviar-documento">
        <label className="space-y-1">
          <span className="text-slate-400">Documento</span>
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-base sm:text-sm text-slate-100" data-testid="documento-categoria">
            {categorias.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-slate-400">Validade que consta no documento</span>
          <input type="date" value={validade} onChange={(e) => setValidade(e.target.value)} className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-base sm:text-sm text-slate-100" data-testid="documento-validade" />
        </label>
        {categoria === "licenca_pu" && (
          <label className="space-y-1">
            <span className="text-slate-400">Validade da licença de instrutor (no mesmo documento)</span>
            <input type="date" value={validadeInstrutor} onChange={(e) => setValidadeInstrutor(e.target.value)} className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-base sm:text-sm text-slate-100" data-testid="documento-validade-instrutor" />
          </label>
        )}
        <div className="space-y-2 sm:col-span-2">
          <span className="block text-slate-400">Ficheiro (foto ou PDF, máx. 10 MB)</span>
          <div className="flex flex-wrap items-center gap-2">
            {/* Abre o seletor do telemóvel (câmara, galeria ou ficheiros). O campo fica escondido por completo: um campo
                de 1 pixel recebia o foco ao voltar do seletor, e o Safari do iPhone fazia zoom para ele (ecrã todo preto). */}
            <button type="button" onClick={() => campoFicheiro.current?.click()} className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-slate-600 bg-slate-800 px-3 text-sm text-slate-100 transition-colors hover:border-sky-400 hover:bg-slate-700" data-testid="documento-escolher">
              <Upload className="h-4 w-4 text-sky-300" />
              Escolher ficheiro
            </button>
            <input ref={campoFicheiro} type="file" accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => escolherFicheiro(e.target.files?.[0] || null)} data-testid="documento-ficheiro" />
            <span className="min-w-0 truncate text-slate-400" data-testid="documento-nome">{ficheiro ? ficheiro.name : "Nenhum ficheiro selecionado"}</span>
          </div>
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" size="sm" disabled={aGravar} data-testid="documento-enviar">Enviar para aprovação</Button>
        </div>
      </form>
    </div>
  );
}
